import { useEffect, useState, useRef, useCallback } from "react";
import { Box, Button, Container, IconButton, Typography, useTheme } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { tokens } from "../../theme";

import type {
  CategoryResponse,
  DesignResponse,
  PaginatedResponse,
} from "./types/figma";
import apiService from "../../service/Api";
import { Header } from "./components/Header";
import { CategoryTabs, FAVOURITE_TAB_ID } from "./components/CategoryTabs";
import { DesignGrid } from "./components/DesignGrid";
import { AddDesignSidebar } from "./components/sidebar/AddDesignSidebar";
import { AddCategorySidebar } from "./components/sidebar/AddCategory";

export default function ListDesigns() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    FAVOURITE_TAB_ID,
  );

  const [categories, setCategories] = useState<CategoryResponse[]>([]);
  const [designs, setDesigns] = useState<DesignResponse[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingDesigns, setIsLoadingDesigns] = useState(true);
  const [isSticky, setIsSticky] = useState(false);

  const [currentUrl, setCurrentUrl] = useState<string | null>(null);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const [previousUrl, setPreviousUrl] = useState<string | null>(null);
  const [designCount, setDesignCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const DESIGNS_PAGE_SIZE = 8;
  const totalPages = Math.max(1, Math.ceil(designCount / DESIGNS_PAGE_SIZE));

  const [designSidebarOpen, setDesignSidebarOpen] = useState(false);
  const [categorySidebarOpen, setCategorySidebarOpen] = useState(false);

  const tabsRef = useRef<HTMLDivElement>(null);
  const tabsPlaceholderRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (!tabsPlaceholderRef.current) return;

    const placeholderRect = tabsPlaceholderRef.current.getBoundingClientRect();

    if (placeholderRect.top <= 0) {
      setIsSticky(true);
    } else {
      setIsSticky(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const fetchCategories = async () => {
    setIsLoadingCategories(true);

    try {
      const res = await apiService.get<PaginatedResponse<CategoryResponse>>(
        "/api/figma-designs/categories/",
      );

      setCategories(res.data.results);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const stripOrigin = (url: string) => url.replace(/^https?:\/\/[^/]+\//, "");

  const fetchDesignsPage = async (url: string, page: number) => {
    setIsLoadingDesigns(true);

    try {
      const res = await apiService.get<PaginatedResponse<DesignResponse>>(url);
      setDesigns(res.data.results);
      setCurrentUrl(url);
      setNextUrl(res.data.next ? stripOrigin(res.data.next) : null);
      setPreviousUrl(res.data.previous ? stripOrigin(res.data.previous) : null);
      setDesignCount(res.data.count);
      setCurrentPage(page);
    } catch (error) {
      console.error("Failed to fetch designs:", error);
    } finally {
      setIsLoadingDesigns(false);
    }
  };

  const fetchDesigns = (categoryId: number | null) => {
    const params = new URLSearchParams({ page_size: String(DESIGNS_PAGE_SIZE) });
    if (categoryId === FAVOURITE_TAB_ID) {
      params.set("is_favourite", "true");
    } else if (categoryId !== null) {
      params.set("category", String(categoryId));
    }

    return fetchDesignsPage(
      `/api/figma-designs/figma-design/?${params.toString()}`,
      1,
    );
  };

  const handleNextPage = () => {
    if (nextUrl) fetchDesignsPage(nextUrl, currentPage + 1);
  };

  const handlePreviousPage = () => {
    if (previousUrl) fetchDesignsPage(previousUrl, currentPage - 1);
  };

  const handleSaveCategory = async (name: string, color: string) => {
    try {
      const res = await apiService.post<CategoryResponse>(
        "/api/figma-designs/categories/",
        {
          name,
          description: "",
          category_color: color,
        },
      );

      await fetchCategories();
      setCategorySidebarOpen(false);

      return res.data;
    } catch (error) {
      console.error("Failed to save category:", error);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchDesigns(FAVOURITE_TAB_ID);
    // These functions intentionally run once when the page mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCategoryChange = (categoryId: number | null) => {
    setSelectedCategoryId(categoryId);
    fetchDesigns(categoryId);
  };

  const handleCreateDesign = async (data: {
    name: string;
    figma_link: string;
    category: number;
    design_type: "design" | "prototype" | "design_prototype";
    design_image?: File;
    description?: string;
  }): Promise<DesignResponse> => {
    const formData = new FormData();
    formData.append("name", data.name);
    formData.append("figma_link", data.figma_link);
    formData.append("category", data.category.toString());
    formData.append("design_type", data.design_type);
    if (data.design_image) formData.append("design_image", data.design_image);

    const res = await apiService.post<DesignResponse>(
      "/api/figma-designs/figma-design/",
      formData,
    );
    fetchDesigns(null);
    return res.data;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: colors.primary[900],
      }}
    >
      <Header onAddDesignClick={() => setDesignSidebarOpen(true)} />

      <Box
        ref={tabsPlaceholderRef}
        sx={{
          height: isSticky ? 100 : "auto",
        }}
      >
        <Box
          ref={tabsRef}
          sx={{
            position: isSticky ? "fixed" : "relative",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            backgroundColor: colors.primary[900],

            transition: "box-shadow 0.2s ease",
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                py: 2,
              }}
            >
              {/* Tabs */}
              <Box sx={{ flex: 1 }}>
                <CategoryTabs
                  categories={categories}
                  selectedCategoryId={selectedCategoryId}
                  onCategoryChange={handleCategoryChange}
                  isLoading={isLoadingCategories}
                  isSticky={isSticky}
                />
              </Box>
              {isSticky && (
                <Button
                  onClick={() => setDesignSidebarOpen(true)}
                  sx={{
                    backgroundColor: colors.primary[900],
                    color: colors.primary[700],
                    px: 3.5,
                    py: 2,

                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: "0px 8px 24px rgba(0,0,0,0.25)",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor: colors.primary[800],
                    },
                  }}
                >
                  Add New Design
                </Button>
              )}{" "}
            </Box>
          </Container>
        </Box>
      </Box>

      {/* Design Grid */}
      <DesignGrid
        designs={designs}
        isLoading={isLoadingDesigns}
        fetchDesigns={() =>
          currentUrl
            ? fetchDesignsPage(currentUrl, currentPage)
            : fetchDesigns(selectedCategoryId)
        }
      />

      {!isLoadingDesigns && designs.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", pb: 6 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 1,
              py: 1,
              borderRadius: "999px",
              backgroundColor: colors.primary[900],
              border: `1px solid ${colors.primary[800]}`,
              boxShadow: "0px 4px 16px rgba(0,0,0,0.06)",
            }}
          >
            <IconButton
              onClick={handlePreviousPage}
              disabled={!previousUrl}
              aria-label="Previous page"
              size="small"
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                color: colors.primary[700],
                border: `1px solid ${colors.primary[800]}`,
                transition: "all 0.15s ease",
                "&:hover": {
                  backgroundColor: colors.primary[400],
                  color: colors.primary[900],
                  borderColor: colors.primary[400],
                },
                "&.Mui-disabled": {
                  color: colors.primary[800],
                  border: "1px solid transparent",
                },
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>

            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: colors.primary[400], minWidth: 96, textAlign: "center" }}
            >
              Page {currentPage} of {totalPages}
            </Typography>

            <IconButton
              onClick={handleNextPage}
              disabled={!nextUrl}
              aria-label="Next page"
              size="small"
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                color: colors.primary[700],
                border: `1px solid ${colors.primary[800]}`,
                transition: "all 0.15s ease",
                "&:hover": {
                  backgroundColor: colors.primary[400],
                  color: colors.primary[900],
                  borderColor: colors.primary[400],
                },
                "&.Mui-disabled": {
                  color: colors.primary[800],
                  border: "1px solid transparent",
                },
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}

      <AddDesignSidebar
        open={designSidebarOpen}
        onClose={() => setDesignSidebarOpen(false)}
        categories={categories}
        onCreateCategory={() => {
          setCategorySidebarOpen(true);
        }}
        onSave={handleCreateDesign}
      />

      <AddCategorySidebar
        open={categorySidebarOpen}
        onClose={() => setCategorySidebarOpen(false)}
        onSave={handleSaveCategory}
      />
    </Box>
  );
}
